import { initializeAnalytics } from '@firebase/analytics';
import { AnalyticsBrowser } from '@segment/analytics-next';
import type {
  AnalyticsCommonProps,
  AnalyticsEvents,
  BalanceProps,
  PurchaseSource,
  TrackEvent,
} from '@sunflower-games/analytics-events';
import { logEvent } from 'firebase/analytics';

import type { Wallet } from '../../dto/wallet.types';
import type { LoyaltyTierKey } from '../../stores/loyalty/types';
import type { EnumPaymentMethods } from '../../stores/payredeem/types';
import { getSecondsSinceAppMount, getSecondsSinceSessionStart } from '../../utils/appMount';
import { devError, devLog } from '../../utils/log';
import { getCookie } from '../../utils/secureCookies';
import { postRequest } from '../api';
import type { Currency } from '../auth';
import { logToNewRelic } from '../debug';
import { firebase, firebaseAuth } from '../firebase';
import { getMediaForceTransactionId, sendGoogleTagFtdEvent, sendGoogleTagSuccessPayment } from '../googleTagEvent';
import { resolveFlag } from '../launchDarkly';
import { platformContextService } from '../platformContext/PlatformContextService';

import { getBrowserSessionId } from './getBrowserSessionId';
import { getTabId } from './getTabId';
import { getDevice, getScreenSize } from './helpers';
import { mapMethodToAnalytics } from './mapMethodToAnalytics';
import { mixpanel } from './mixpanel/mixpanelSdk';
import type { AnalyticsExtendedCommonProps } from './types';

const SEGMENT_APP_KEY = import.meta.env.VITE_SEGMENT_APP_KEY;

class Analytics {
  private service = initializeAnalytics(firebase, {
    config: {
      send_page_view: false,
      // https://developers.google.com/search/blog/2020/01/get-ready-for-new-samesitenone-secure?hl=en
      cookie_flags: 'max-age=7200;secure;samesite=none',
    },
  });

  private segmentAnalytics = new AnalyticsBrowser();

  private isIdentified = false;

  public init() {
    try {
      if (SEGMENT_APP_KEY) {
        this.setCommonProps({
          facebook_fbc: getCookie('_fbc'),
          facebook_fbp: getCookie('_fbp'),
        });
        void this.segmentAnalytics.load(
          {
            writeKey: SEGMENT_APP_KEY,
          },
          {
            highEntropyValuesClientHints: [
              'architecture',
              'bitness',
              'model',
              'platformVersion',
              'uaFullVersion',
              'fullVersionList',
              'wow64',
            ],
          },
        );
        getBrowserSessionId();
      } else {
        logToNewRelic('Segment.init', 'Segment app key is not set', 'error');
      }
    } catch (e) {
      logToNewRelic('Segment.init', 'Failed to initialize segment', 'error');
    }
    // Second destination while we migrate off Segment — gated on its own token, independent of Segment's key.
    mixpanel.init();
  }

  public async identify(firebaseId: string) {
    if (!this.isIdentified) {
      try {
        void mixpanel.identify(firebaseId);
        await this.segmentAnalytics.identify(firebaseId, null, { app: { version: __APP_VERSION__ } });
        this.isIdentified = true;
      } catch (error) {
        logToNewRelic('Segment.identify', 'Failed to identify user for segment', 'error');
      }
    }
  }

  public async logout() {
    if (this.isIdentified) {
      try {
        void mixpanel.reset();
        await this.segmentAnalytics.reset();
        this.isIdentified = false;
      } catch (error) {
        logToNewRelic('Segment.logout', 'Failed to logout user', 'error');
      }
    }
  }

  private commonProps: Partial<AnalyticsCommonProps> = {
    lobby_type: 'CC',
  };

  public setCommonProps(props: Partial<AnalyticsCommonProps>) {
    this.commonProps = {
      ...this.commonProps,
      ...props,
    };
  }

  private balanceProps: BalanceProps = {
    social_cc: 0,
    coins_usc: 0,
    coins_bsc: 0,
    coins_rsc: 0,
  };

  public setBalanceProps(props: Wallet) {
    this.balanceProps = {
      social_cc: props.gcCoin,
      coins_rsc: props.scCoin.wsc,
      coins_bsc: props.scCoin.bsc,
      coins_usc: props.scCoin.psc,
    };
  }

  public formatCommonProps(): Partial<AnalyticsExtendedCommonProps> {
    return {
      ...this.commonProps,
      referrer: document.referrer,
      device: getDevice(),
      platform: platformContextService.getConfig().platform,
      screen_size: getScreenSize(),
      user_id: firebaseAuth.currentUser?.uid,
      browser_session_id: getBrowserSessionId(),
      tab_session_id: getTabId(),
      session_time_secs: getSecondsSinceSessionStart(),
      since_app_mount_secs: getSecondsSinceAppMount(),
      event_timestamp: new Date().toISOString(),
    };
  }

  public sendEvent: TrackEvent = async (eventName, paramsArg) => {
    const params = {
      ...this.formatCommonProps(),
      ...this.balanceProps,
      ...paramsArg,
    };
    devLog(
      'sendEvent',
      `Event: ${eventName}${params.step_name ? `, Step: ${params.step_name}` : ''}${params.event_action ? `, Action: ${params.event_action}` : ''}`,
      params,
    );
    logEvent<string>(this.service, String(eventName), params);
    void mixpanel.track(String(eventName), params);
    await this.segmentAnalytics.track(String(eventName), params);
  };

  // Minigames fire distinctly-named events (the contract's event_name), not the legacy 'widget' bucket,
  // so the name is dynamic and isn't a key in AnalyticsEvents. Delegate to sendEvent so the common-prop
  // and balance enrichment lives in one place; the cast only loosens the keyed-name constraint.
  public sendMiniGameEvent = (
    eventName: string,
    params: {
      event_parent?: 'minigames';
      event_category?: string;
      event_action?: 'impression' | 'click' | 'spin_end';
      event_type?: string;
      event_value?: string;
      button_text?: string;
    },
  ) => this.sendEvent(eventName as keyof AnalyticsEvents, params as AnalyticsEvents[keyof AnalyticsEvents]);

  // Redeem-redesign taxonomy (redeem_page/redeem/cancel_request/…) is not yet keyed in
  // AnalyticsEvents. Same pattern as sendMiniGameEvent: keep the cast in one place until the
  // shared @sunflower-games/analytics-events package defines these events.
  public sendRedeemEvent = (
    eventName: string,
    params: {
      event_parent?: 'redeem';
      event_category?: 'redeem' | 'redeem_cash';
      event_action?: 'impression' | 'click';
      event_type?: 'coin_type' | 'payment_platform' | 'redeem_amount';
      event_value?: string;
    },
  ) => this.sendEvent(eventName as keyof AnalyticsEvents, params as AnalyticsEvents[keyof AnalyticsEvents]);

  // Stamp Card's taxonomy (widget/popup/popup_close/info under event_parent 'features',
  // event_category 'stamp_card') isn't keyed in the shared @sunflower-games/analytics-events package
  // yet. Same pattern as sendMiniGameEvent/sendRedeemEvent: keep the cast in one place, and keep the
  // spec's four event names and their allowed properties enforced by this signature.
  public sendStampCardEvent = (
    eventName: 'widget' | 'popup' | 'popup_close' | 'info',
    params: {
      event_parent: 'features';
      event_category: 'stamp_card';
      event_action: 'impression' | 'click';
      click_location?: 'lobby' | 'store';
      /** Paired with `event_value` — the spec carries stamp progress as a type/value pair. */
      event_type?: 'stamps_collected';
      event_value?: string;
      button_text?: string;
    },
  ) => this.sendEvent(eventName as keyof AnalyticsEvents, params as AnalyticsEvents[keyof AnalyticsEvents]);

  public sendFailedPurchaseEvent(data?: any) {
    void this.sendEvent('purchase', {
      ...data,
      status: 0,
    });
  }

  public sendSuccessPurchaseEvent(data: {
    currency: Currency;
    amount: number;
    packageId: number;
    source: PurchaseSource;
    store_key: string;
    sign_up_date: string;
    ftd: boolean;
    isPvpPurchase: boolean;
    eventParams: {
      transaction_id: string | number;
      payment_method: EnumPaymentMethods | null;
      social_cc_package: number;
      coins_usc_package: number;
      store_segment: LoyaltyTierKey;
    };
    gtagParams: Record<string, any>;
  }) {
    try {
      const { purchase: purchaseFF, ftd: ftdFF } = resolveFlag('purchase_event_debug', {
        purchase: 'both',
        ftd: 'both',
      });

      logToNewRelic(
        'sendSuccessPurchaseEvent',
        {
          message: 'event start',
          data,
        },
        'info',
      );

      if (!purchaseFF || purchaseFF === 'both' || purchaseFF === 'google_tag') {
        // google tag
        sendGoogleTagSuccessPayment({
          ...data.gtagParams,
          ftd: data.ftd,
          value: data.amount,
          currency: data.currency,
          isPvpPurchase: data.isPvpPurchase,
          sign_up_date: data.sign_up_date,
        });
      }
      if (!purchaseFF || purchaseFF === 'both' || purchaseFF === 'firebase') {
        // firebase analytics
        void this.sendEvent('purchase', {
          step_name: 'purchase',
          is_succeeded: 1,
          payment_method: mapMethodToAnalytics(data.eventParams.payment_method),
          package_id: data.packageId,
          transaction_id: data.eventParams.transaction_id,
          provider: 'unknown',
          store_segment: data.eventParams.store_segment,
          amount: data.amount,
          is_ftd: data.ftd ? 1 : 0,
          // GA4 requirements
          currency: data.currency,
          value: data.amount,
          source: data.source,
          store_key: data.store_key,
          sign_up_date: data.sign_up_date,
        });
      }

      if (data.ftd) {
        if (!ftdFF || ftdFF === 'both' || ftdFF === 'google_tag') {
          sendGoogleTagFtdEvent({
            ...data.gtagParams,
            ftd: data.ftd,
            value: data.amount,
            currency: data.currency,
          });
        }
        if (!ftdFF || ftdFF === 'both' || ftdFF === 'firebase') {
          // firebase analytics + segment/mixpanel
          void this.sendEvent('ftd', {
            payment_method: mapMethodToAnalytics(data.eventParams.payment_method),
            package_id: data.packageId,
            transaction_id: data.eventParams.transaction_id,
            store_segment: data.eventParams.store_segment,
            amount: data.amount,
            // GA4 requirements
            currency: data.currency,
            value: data.amount,
            source: data.source,
            store_key: data.store_key,
            sign_up_date: data.sign_up_date,
          });
        }

        const mediaForceTransactionId = getMediaForceTransactionId();
        if (mediaForceTransactionId) {
          this.sendMediaForceEvent('ftd', {
            transactionId: mediaForceTransactionId,
          });
        }
      }

      logToNewRelic('sendSuccessPurchaseEvent', { message: 'event end' }, 'info', { isFtd: data.ftd });
    } catch (e) {
      logToNewRelic(
        'sendSuccessPurchaseEvent',
        {
          message: 'event fail',
          data: JSON.stringify(e, null, 2),
        },
        'error',
      );
      devError('sendSuccessPurchaseEvent error', e);
    }
  }

  sendMediaForceEvent(eventName: 'sign_up' | 'ftd', eventData: Record<'transactionId', string>) {
    try {
      void postRequest('/v2/user/mediaForceEvent', {
        eventName,
        eventData: {
          transactionId: eventData.transactionId,
        },
      });
    } catch (e) {
      logToNewRelic('sendMediaForceEvent', 'MediaForce event failed to send', 'debug');
    }
  }

  getSegmentAnonymousId() {
    return this.segmentAnalytics.instance?.user().anonymousId();
  }
}

export const analytics = new Analytics();
